import React, { useEffect, useState } from 'react';
import { Modal, Form, Button } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faSpinner, faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import sanitizeFilename from 'sanitize-filename';

import { useForm } from '../../../hooks/useForm';
import { useRenameClaimFile } from '../../../services/claims';

const RenameFileModal = ({ show, toggle, file, claimId, onSuccess }) => {
  const { form, onChange, setValues } = useForm({ fileName: '' });
  const [validationError, setValidationError] = useState('');

  const renameFile = useRenameClaimFile(
    () => {
      if (typeof onSuccess === 'function') {
        onSuccess();
      }
      toggle();
    },
  );

  useEffect(() => {
    if (file && file.fileName) {
      setValues({ fileName: file.fileName }, true);
    } else {
      setValues({ fileName: '' }, true);
    }
    setValidationError('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [file, show]);

  const validateFileName = (name) => {
    const trimmed = name.trim();

    if (!trimmed) {
      return 'File name cannot be empty.';
    }

    const sanitized = sanitizeFilename(trimmed);

    if (sanitized !== trimmed) {
      return 'File name contains invalid characters.';
    }

    if (trimmed.length > 255) {
      return 'File name cannot exceed 255 characters.';
    }

    return '';
  };

  const handleChange = (e) => {
    onChange(e);
    setValidationError(validateFileName(e.target.value));
  };

  const onSubmit = (e) => {
    e.preventDefault();

    const error = validateFileName(form.fileName);
    if (error) {
      setValidationError(error);
      return;
    }

    renameFile.mutate({
      claimId,
      fileId: file.fileId,
      fileName: form.fileName.trim(),
    });
  };

  const isValid = !validationError && form.fileName.trim() && form.fileName !== file?.fileName;

  return (
    <Modal show={show} onHide={() => toggle()}>
      <Modal.Header closeButton>
        <Modal.Title>Rename File</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <form onSubmit={onSubmit}>
          <Form.Group className="mb-3">
            <Form.Label>File Name</Form.Label>
            <Form.Control
              type="text"
              name="fileName"
              value={form.fileName}
              onChange={handleChange}
              placeholder="Enter new file name"
              autoFocus
              isInvalid={!!validationError}
            />
            <Form.Text className={validationError ? 'text-danger' : 'text-muted'}>
              {validationError || `${form.fileName.length}/255 characters`}
            </Form.Text>
          </Form.Group>
          {validationError && (
            <div className="alert alert-warning d-flex align-items-center mb-3">
              <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
              {validationError}
            </div>
          )}
          <hr />
          <Button
            variant="success"
            type="submit"
            disabled={renameFile.isLoading || !isValid}
          >
            <FontAwesomeIcon
              icon={renameFile.isLoading ? faSpinner : faCheck}
              spin={renameFile.isLoading}
            />{' '}
            Rename
          </Button>{' '}
          <Button variant="link" onClick={() => toggle()}>
            Cancel
          </Button>
        </form>
      </Modal.Body>
    </Modal>
  );
};

export default RenameFileModal;
